import type { MultiValue, Option } from '@ksyos/design-system';
import type { JSX } from 'react';
import type { ActionMeta } from 'react-select';
import { FormGroup, FormParagraph, FormSection, Select } from '@ksyos/design-system';
import type { BariPreMdoReportData } from '@common/validation/customControls/bariPre/bariPreMdoReport/v1/bariPreMdoReportSchemaV1';
import type { BariPreMedConsultAssessmentData } from '@common/validation/customControls/bariPre/bariPreMedConsultAssessment/v1/bariPreMedConsultAssessmentSchemaV1';
import type { BariPreResumeMdoData } from '@common/validation/customControls/bariPre/bariPreResumeMdo/v1/bariPreResumeMdoSchemaV1';
import type { IndicatieVerwijzing } from '@common/validation/customControls/bariPre/utils';
import { referralIndications } from '@common/customControls/bariatrics';
import { useProcessActivityEditContext } from '@frontend/utils/hooks/useProcessActivityContext';
import { TextAreaFormGroup } from '../processActivityFormGroupFields';

type Data = BariPreMedConsultAssessmentData | BariPreMdoReportData | BariPreResumeMdoData;

type ReferralIndicationOption = {
    key: keyof IndicatieVerwijzing;
    label: string;
};

const referralIndicationOptions: ReferralIndicationOption[] = [
    ...referralIndications,
    { key: 'indicatie_voor_verwijzing_overig', label: 'Overig' },
];

export function IndicatiesVoorVerwijzing(): JSX.Element {
    const { data, onChange } = useProcessActivityEditContext<Data>();

    return (
        <FormSection title="Indicatie voor verwijzing">
            <FormGroup label="Indicatie voor verwijzing">
                <Select
                    isClearable={true}
                    isMulti={true}
                    onChange={handleChange}
                    options={referralIndicationOptions.map(mapReferralIndicationToOption)}
                    placeholder="Selecteer een indicatie..."
                    value={referralIndicationOptions
                        .filter((referralIndication) => data[referralIndication.key])
                        .map(mapReferralIndicationToOption)}
                />
            </FormGroup>
            {referralIndications
                .filter((referralIndication) => data[referralIndication.key])
                .map(({ key, label, text }) => (
                    <FormGroup
                        key={key}
                        label={`Indicatie voor verwijzing ${label.toLowerCase()}`}
                    >
                        <FormParagraph>{text}</FormParagraph>
                    </FormGroup>
                ))}
            {data.indicatie_voor_verwijzing_overig && (
                <TextAreaFormGroup
                    fieldKey="indicatie_voor_verwijzing_overig_toelichting"
                    isRequired={true}
                    label="Overige indicatie voor verwijzing"
                />
            )}
        </FormSection>
    );

    function mapReferralIndicationToOption(referralIndication: ReferralIndicationOption): Option {
        return {
            label: referralIndication.label,
            value: referralIndication.key,
        };
    }

    function handleChange(_newValue: MultiValue<Option>, actionMeta: ActionMeta<Option>): void {
        if (actionMeta.action === 'select-option' && actionMeta?.option?.value) {
            return onChange({ [actionMeta.option.value]: true }, [actionMeta.option.value]);
        }
        if (actionMeta.action === 'remove-value' && actionMeta?.removedValue?.value) {
            const dataToChange: Partial<Data> = {
                [actionMeta.removedValue.value]: false,
            };

            if (actionMeta.removedValue.value === 'indicatie_voor_verwijzing_overig') {
                dataToChange.indicatie_voor_verwijzing_overig_toelichting = null;
            }

            return onChange(dataToChange, [actionMeta.removedValue.value]);
        }
        if (actionMeta.action === 'clear') {
            const dataToChange: Record<string, boolean | null> = { indicatie_voor_verwijzing_overig_toelichting: null };
            for (const referralIndication of referralIndicationOptions) {
                dataToChange[referralIndication.key] = false;
            }

            return onChange(dataToChange, Object.keys(data));
        }
    }
}
